import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as AuthenticationActions from '@iw-shared/store/actions/authentication.action';
import * as StorageActions from '@iw-shared/store/actions/storage.action';
import * as UserActions from '@iw-shared/store/actions/user.action';
import {concatMap, map, switchMap, take, tap} from 'rxjs/operators';
import {AuthenticationService} from '@iw-shared/core/services/authentication.service';
import {AppState} from '@iw-shared/store/state/app.state';
import {Store} from '@ngrx/store';
import {resetUser} from '@iw-shared/store/actions/user.action';
import {
  deleteTokens,
  setAccessExpire,
  setAPIConfig, setAPIEndpoint, setEntryPoint,
  setRefreshExpire, setScopes,
  userId
} from '@iw-shared/store/actions/storage.action';
import {Router} from '@angular/router';
import {EMPTY, of} from "rxjs";
import {setNotification} from "@iw-shared/store/actions/global.actions";
import {error} from "ng-packagr/lib/utils/log";

@Injectable()
export class AuthenticationEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private authenticationService: AuthenticationService,
    private router: Router) {
  }

  login$ = createEffect(() => this.actions$.pipe(
      ofType(AuthenticationActions.login),
      switchMap((action) => {

        this.store.dispatch(setAPIEndpoint({endpoint: action.endpoint}))

        switch (action.endpoint) {
          case 'admin':
            return this.authenticationService.loginAccount(action.username, action.password).pipe(
              map((r) => {
                if (r.result?.accountBackendUserTokens.data.expiredIn) {
                  this.authenticationService.setAccessTokenCookie();
                  console.log(r.result?.accountBackendUserTokens.data);
                  this.store.dispatch(setEntryPoint({entryPoint: r.result?.accountBackendUserTokens.data.entryPoint ?? null}));
                  this.store.dispatch(setScopes({scopes: r.result?.accountBackendUserTokens.data.scopes ?? null}));
                  return AuthenticationActions.loginSuccess(
                    {
                      config: r.result?.accountBackendUserTokens.data.config,
                      userId: r.result?.accountBackendUserTokens.data.userId,
                      accessExpiredIn: r.result?.accountBackendUserTokens.data.expiredIn,
                    }
                  )
                }
                this.authenticationService.deleteAccessTokenCookie();
                return AuthenticationActions.loginFailure(
                  {errorMsg: r.error?.message || '[login] Es ist ein unbekannter Fehler aufgetreten.'}
                )
              })
            );
          default:
            return this.authenticationService.login(action.username, action.password).pipe(
              map((r) => {
                if (r.result?.userTokens.data.expiredIn) {
                  this.authenticationService.setAccessTokenCookie();
                  this.store.dispatch(setScopes({scopes: r.result?.userTokens.data.scopes ?? null}));
                  this.store.dispatch(setEntryPoint({entryPoint: r.result?.userTokens.data.entryPoint ?? null}));
                  return AuthenticationActions.loginSuccess(
                    {
                      config: r.result?.userTokens.data.config,
                      userId: r.result?.userTokens.data.userId,
                      accessExpiredIn: r.result?.userTokens.data.expiredIn
                    }
                  )
                }
                this.authenticationService.deleteAccessTokenCookie();
                return AuthenticationActions.loginFailure(
                  {errorMsg: r.error?.message || '[login] Es ist ein unbekannter Fehler aufgetreten.'}
                )
              })
            )
        }
      }),
    )
  );

  loginSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AuthenticationActions.loginSuccess),
    map((action) => {
        this.store.dispatch(setAPIConfig({config: action.config}));
        this.store.dispatch(setAccessExpire({expiredIn: action.accessExpiredIn}));
        this.store.dispatch(setRefreshExpire({expiredIn: 60 * 60 * 24 * 24}));
        //return StorageActions.userId({userId: '-'});
        return UserActions.setUser({userId: action.userId});
      }
    )
  ));

  refreshToken = createEffect(() => this.actions$.pipe(
    ofType(AuthenticationActions.refreshToken),
    switchMap(() => {
      let endpoint: 'admin' | 'account' | 'productDB' | undefined = undefined;
      this.store.select(state => state.storage.APIEndpoint).pipe(
        take(1)
      ).subscribe(
        r => {
          if (r) {
            endpoint = r;
          }
        }
      );
      if (!endpoint) {
        this.store.dispatch(setNotification({notification: 'Endpoint undefined', notificationType: 'error'}));
        return EMPTY;
      }
      switch (endpoint) {
        case 'admin':
          return this.authenticationService.refreshTokenAccount()
            .pipe(
              map((r) => {
                if (r.result) {
                  return AuthenticationActions.refreshTokenSuccess(
                    {
                      accessExpiredIn: r.result?.accountBackendUserTokens.data.expiredIn
                    }
                  )
                } else {
                  this.authenticationService.deleteAccessTokenCookie();
                  return AuthenticationActions.refreshTokenFailure()
                }
              })
            );
        default:
          return this.authenticationService.refreshToken()
            .pipe(
              map((r) => {
                if (r.result) {
                  return AuthenticationActions.refreshTokenSuccess(
                    {
                      accessExpiredIn: r.result?.userTokens.data.expiredIn
                    }
                  )
                } else {
                  this.authenticationService.deleteAccessTokenCookie();
                  return AuthenticationActions.refreshTokenFailure()
                }
              })
            );
      }
    })
  ));

  refreshSuccess = createEffect(() => this.actions$.pipe(
    ofType(AuthenticationActions.refreshTokenSuccess),
    map((action) => {
        this.store.dispatch(setAccessExpire({expiredIn: action.accessExpiredIn}));
        return StorageActions.setRefreshExpire({expiredIn: 60 * 60 * 24 * 24});
      }
    )
  ));

  refreshTokenFailure = createEffect(() => this.actions$.pipe(
    ofType(AuthenticationActions.refreshTokenFailure),
    map((action) => {
        return AuthenticationActions.logout();
      }
    )
  ));

  logout$ = createEffect(() => this.actions$.pipe(
    ofType(AuthenticationActions.logout),
    tap(() => {
      this.store.dispatch(resetUser());
      this.store.dispatch(deleteTokens());
      this.store.dispatch(userId({userId: null}));
    }),
    switchMap(() =>

      of(this.authenticationService.isAccessTokenCookie()).pipe(
        switchMap(isAccessToken => {
          let endpoint: 'admin' | 'account' | 'productDB' | undefined = undefined;
          this.store.select(state => state.storage.APIEndpoint).pipe(
            take(1)
          ).subscribe(
            r => {
              if (r) {
                endpoint = r;
              }
            }
          );
          if (!isAccessToken) {
            return of(AuthenticationActions.logoutFailure());
          }
          if (!endpoint) {
            this.store.dispatch(setNotification({notification: 'Endpoint undefined', notificationType: 'error'}));
            return EMPTY;
          }
          switch (endpoint) {
            case 'admin':
              return this.authenticationService.logoutAccount().pipe(
                map(r => {
                  this.authenticationService.deleteAccessTokenCookie();
                  if (r.result?.accountBackendUserTokens.deleted) {
                    return AuthenticationActions.logoutSuccess();
                  }
                  return AuthenticationActions.logoutFailure();
                })
              );
            default:
              // Proceed with logout
              return this.authenticationService.logout().pipe(
                map(r => {
                  this.authenticationService.deleteAccessTokenCookie();
                  if (r.result?.userTokens.deleted) {
                    return AuthenticationActions.logoutSuccess();
                  }
                  return AuthenticationActions.logoutFailure();
                })
              );
          }

        })
      )
    )
  ));

  logoutSuccess$ = createEffect(() => this.actions$.pipe(
      ofType(AuthenticationActions.logoutSuccess),
      tap(() => {
          console.log('logout success');
          this.router.navigate(["/login"]).then();
          //this.router.navigate(['/']).then();
        }
      )
    ), {dispatch: false}
  );
}
